import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { req } from "../api";

/**
 * Small helpers
 */
function pretty(v) {
  return JSON.stringify(v, null, 2);
}


function pick(obj, paths) {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    let ok = true;
    for (const part of parts) {
      if (!cur || typeof cur !== "object" || !(part in cur)) {
        ok = false;
        break;
      }
      cur = cur[part];
    }
    if (ok && cur !== undefined && cur !== null) return cur;
  }
  return null;
}

function genSecret(len = 20) {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function buildValues(fields, valueMap) {
  return (fields || []).map((f) => ({
    key: String(f._id),
    value: valueMap[String(f._id)] ?? "",
  }));
}

function validateRequired(fields, valueMap) {
  const errors = [];
  for (const f of fields || []) {
    if (f.required) {
      const v = (valueMap[String(f._id)] ?? "").trim();
      if (!v) errors.push({ fieldId: String(f._id), name: f.name, err: "required" });
    }
  }
  return errors;
}

function summarizeRoutes(resp) {
  const raw = pick(resp, ["data.data.routes", "data.data.list", "data.data", "data.result", "data.routes"]) || [];
  const arr = Array.isArray(raw) ? raw : [];
  return {
    ok: resp?.ok ?? null,
    success: resp?.data?.success ?? resp?.data?.data?.success ?? null,
    count: arr.length,
    first3: arr.slice(0, 3).map((r) => ({
      _id: r?._id,
      from: r?.from?.name || r?.from?.symbol || r?.from?.xml,
      to: r?.to?.name || r?.to?.symbol || r?.to?.xml,
    })),
  };
}

function extractPaymentLinks(orderRespJson, lang) {
  const d = orderRespJson?.data?.data || orderRespJson?.data?.result || orderRespJson?.data || orderRespJson;

  const uid = pick(d, ["orderUID", "uid", "order.uid", "orderUIDNumber", "orderUid", "order.uidNumber"]);
  const secret = pick(d, ["orderSecret", "secret", "order.secret", "access.secret", "orderSecretString"]);

  const lng = (lang || "en").toLowerCase();
  if (uid && secret) {
    return {
      paymentUrl: `https://www.poteme.com/${lng}/payment/${uid}/${secret}`,
      orderUrl: `https://www.poteme.com/${lng}/order/${uid}/${secret}`,
    };
  }
  return { paymentUrl: null, orderUrl: null };
}

function extractOrderIds(orderRespJson) {
  const d = orderRespJson?.data?.data || orderRespJson?.data?.result || orderRespJson?.data || orderRespJson;

  const orderUID = pick(d, ["orderUID", "uid", "order.uid", "orderUIDNumber", "orderUid"]);
  const orderSecret = pick(d, ["orderSecret", "secret", "order.secret", "access.secret"]);
  const orderId = pick(d, ["orderId", "id", "_id", "order._id", "order.id"]);

  return {
    orderUID: orderUID ? Number(orderUID) : null,
    orderSecret: orderSecret ? String(orderSecret) : "",
    orderId: orderId ? String(orderId) : "",
  };
}

export default function App() {
  const [loading, setLoading] = useState(false);

  // ====== auth/wallet ======
  const [authState, setAuthState] = useState(null);
  const [sessionResp, setSessionResp] = useState(null);
  const [balanceResp, setBalanceResp] = useState(null);

  const wallets = useMemo(() => balanceResp?.data?.data?.wallets || [], [balanceResp]);
  const [selectedWalletId, setSelectedWalletId] = useState("");
  const selectedWallet = useMemo(
    () => wallets.find((w) => w?._id === selectedWalletId) || wallets[0] || null,
    [wallets, selectedWalletId]
  );

  const [network, setNetwork] = useState("");
  const [addressResp, setAddressResp] = useState(null);
  const [addressAutoResp, setAddressAutoResp] = useState(null);

  // ====== exchanger/invoice ======
  const [routesResp, setRoutesResp] = useState(null);

  // search + limit
  const [routeSearch, setRouteSearch] = useState("");
  const [routesLimit, setRoutesLimit] = useState(200);

  const routesRaw = useMemo(
    () => pick(routesResp, ["data.data.routes", "data.data.list", "data.data", "data.result", "data.routes"]) || [],
    [routesResp]
  );

  const routesFiltered = useMemo(() => {
    const q = routeSearch.trim().toLowerCase();
    const arr = Array.isArray(routesRaw) ? routesRaw : [];
    if (!q) return arr;

    const normCur = (c) => [c?.name, c?.symbol, c?.xml].filter(Boolean).join(" ").toLowerCase();

    return arr.filter((r) => {
      const from = normCur(r?.from);
      const to = normCur(r?.to);
      const id = String(r?.routeId || r?._id || "").toLowerCase();

      return from.includes(q) || to.includes(q) || id.includes(q);
    });
  }, [routesRaw, routeSearch]);

  const routesShown = useMemo(() => {
    const n = Math.max(1, Number(routesLimit) || 200);
    return routesFiltered.slice(0, n);
  }, [routesFiltered, routesLimit]);

  const [selectedRouteId, setSelectedRouteId] = useState("");
  const [routeOneResp, setRouteOneResp] = useState(null);
  const route = useMemo(
    () => pick(routeOneResp, ["data.data.route", "data.route", "data.data", "data.result"]) || null,
    [routeOneResp]
  );

  const routeFields = route?.fields || [];
  const fromFields = route?.from?.fields || [];
  const toFields = route?.to?.fields || [];

  // maps: fieldId -> value
  const [routeValueMap, setRouteValueMap] = useState({});
  const [fromValueMap, setFromValueMap] = useState({});
  const [toValueMap, setToValueMap] = useState({});

  const [amount, setAmount] = useState("100");
  const [lang, setLang] = useState("en");
  const [partner, setPartner] = useState("");
  const [hideOutData, setHideOutData] = useState(false);
  const [disableEmailNotify, setDisableEmailNotify] = useState(false);
  const [skipPreview, setSkipPreview] = useState(false);

  const [clientCallbackUrl, setClientCallbackUrl] = useState("https://example.com/return");
  const [ipnUrl, setIpnUrl] = useState("https://example.com/api/poteme/ipn");
  const [ipnSecret, setIpnSecret] = useState(genSecret());

  const [createOrderResp, setCreateOrderResp] = useState(null);
  const [ipnEventsResp, setIpnEventsResp] = useState(null);

  const [showFullRoutesJson, setShowFullRoutesJson] = useState(false);
  const [showPaymentIframe, setShowPaymentIframe] = useState(false);

  // ====== AUTO STATUS CHECK (polling) ======
  const [autoStatusCheck, setAutoStatusCheck] = useState(true);
  const [orderStatus, setOrderStatus] = useState("");
  const [orderStatusRaw, setOrderStatusRaw] = useState(null);

  const links = useMemo(() => extractPaymentLinks(createOrderResp, lang), [createOrderResp, lang]);

  async function loadState() {
  const out = await req("/api/user/poteme/auth/state");

  const j = out.json || null;

  // нормализуем: если сервер возвращает data-объект, подмешаем его наверх
  const merged = j?.data && typeof j.data === "object"
    ? { ...j, ...j.data }
    : j;

  setAuthState(merged);
}




  useEffect(() => {
    loadState().catch(() => {});
  }, []);

  // pull defaults from server env + auto-select deposit route (if configured)
  useEffect(() => {
    if (!authState) return;

    if (authState.clientCallbackUrl && clientCallbackUrl === "https://example.com/return") {
      setClientCallbackUrl(authState.clientCallbackUrl);
    }
    if (authState.ipnUrl && ipnUrl === "https://example.com/api/poteme/ipn") {
      setIpnUrl(authState.ipnUrl);
    }

    if (authState.depositRouteId && !selectedRouteId) {
      setSelectedRouteId(authState.depositRouteId);
      loadRouteOne(authState.depositRouteId).catch(() => {});
      setRouteSearch("tether trc-20 usdt wallet");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);


async function loadSession() {
  setLoading(true);
  try {
    const out = await req("/api/user/poteme/auth/session/get");
    setSessionResp(out.json);
  } finally {
    setLoading(false);
  }
}

async function loadBalance() {
  setLoading(true);
  try {
    const out = await req("/api/user/poteme/wallets/balance");
    setBalanceResp(out.json);
    const ws = out.json?.data?.data?.wallets || [];
    if (ws.length && !selectedWalletId) setSelectedWalletId(ws[0]._id);
  } finally {
    setLoading(false);
  }
}

async function loadAddress() {
  if (!selectedWallet?._id) return;
  setLoading(true);
  try {
    const out = await req(
      `/api/user/poteme/wallets/address?walletId=${encodeURIComponent(selectedWallet._id)}&network=${encodeURIComponent(network)}`
    );
    setAddressResp(out.json);
  } finally {
    setLoading(false);
  }
}

async function loadAddressAuto() {
  if (!selectedWallet?._id) return;
  setLoading(true);
  try {
    const out = await req(
      `/api/user/poteme/wallets/address-auto?walletId=${encodeURIComponent(selectedWallet._id)}&network=${encodeURIComponent(network)}`
    );
    setAddressAutoResp(out.json);
  } finally {
    setLoading(false);
  }
}


  async function loadRoutes() {
    setLoading(true);
    try {
      const out = await req(`/api/poteme/public/exchanger/route/get?lang=${encodeURIComponent(lang)}`);
      setRoutesResp(out.json);

      const list =
        pick(out.json, ["data.data.routes", "data.data.list", "data.data", "data.result", "data.routes"]) || [];
      if (Array.isArray(list) && list.length && !selectedRouteId) {
  const rid = String(list[0].routeId || list[0]._id || "").trim();
  if (rid) {
    setSelectedRouteId(rid);
    loadRouteOne(rid).catch(() => {});
  }
}

    } finally {
      setLoading(false);
    }
  }

  async function loadRouteOne(routeId) {
    const rid = String(routeId || selectedRouteId || "").trim();
    if (!rid) return;
    setLoading(true);
    try {
      const out = await req(
        `/api/poteme/public/exchanger/route/get/one?routeId=${encodeURIComponent(rid)}&lang=${encodeURIComponent(lang)}`
      );
      setRouteOneResp(out.json);

      // reset maps for new fields
      const r = pick(out.json, ["data.data.route", "data.route", "data.data", "data.result"]) || null;
      if (r) {
        const rf = r?.fields || [];
        const ff = r?.from?.fields || [];
        const tf = r?.to?.fields || [];

        const initMap = (arr) => {
          const m = {};
          for (const f of arr) m[String(f._id)] = "";
          return m;
        };

        setRouteValueMap(initMap(rf));
        setFromValueMap(initMap(ff));
        setToValueMap(initMap(tf));
      }
    } finally {
      setLoading(false);
    }
  }

  function updateMap(setter, id, value) {
    setter((prev) => ({ ...prev, [String(id)]: value }));
  }

  async function createOrder() {
    if (!selectedRouteId) return;

    const e1 = validateRequired(routeFields, routeValueMap);
    const e2 = validateRequired(fromFields, fromValueMap);
    const e3 = validateRequired(toFields, toValueMap);
    const errs = [
      ...e1.map((x) => ({ ...x, type: "routeValues" })),
      ...e2.map((x) => ({ ...x, type: "fromValues" })),
      ...e3.map((x) => ({ ...x, type: "toValues" })),
    ];
    if (errs.length) {
      setCreateOrderResp({ ok: false, error: "Fill required fields", requiredErrors: errs });
      return;
    }

    const payload = {
      routeId: selectedRouteId,
      partner: partner || "",
      amount: Number(amount),

      routeValues: buildValues(routeFields, routeValueMap),
      fromValues: buildValues(fromFields, fromValueMap),
      toValues: buildValues(toFields, toValueMap),

      lang,
      agreement: true,

      hideOutData,
      disableEmailNotify,
      skipPreview,

      clientCallbackUrl: clientCallbackUrl || "",
      ipnUrl: ipnUrl || "",
      ipnSecret: ipnSecret || "",

      additionalServices: [],
      extraData: {},
    };

    setLoading(true);
    try {
      const out = await req("/api/user/invoices/create", { method: "POST", body: payload });

      setCreateOrderResp(out.json?.poteme || out.json);

      setShowPaymentIframe(false);

      // reset status display on new order
      setOrderStatus("");
      setOrderStatusRaw(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadIpnEvents() {
    setLoading(true);
    try {
      const out = await req("/api/poteme/ipn/events");
      setIpnEventsResp(out.json);
    } finally {
      setLoading(false);
    }
  }

  async function checkOrderStatus() {
    const ids = extractOrderIds(createOrderResp);
    if (!ids.orderUID && !ids.orderId) return;

    const qs = new URLSearchParams();
    qs.set("lang", lang);
    if (ids.orderUID) qs.set("orderUID", String(ids.orderUID));
    if (ids.orderSecret) qs.set("orderSecret", ids.orderSecret);
    if (ids.orderId) qs.set("orderId", ids.orderId);

    const out = await req(`/api/poteme/public/exchanger/order/get?${qs.toString()}`);
    setOrderStatusRaw(out.json);

    const st =
      pick(out.json, [
        "data.data.order.status",
        "data.data.status",
        "data.order.status",
        "data.status",
        "data.data.order.newStatus",
      ]) || "";

    setOrderStatus(String(st));
  }

  // Auto polling after invoice created
  useEffect(() => {
    const ids = extractOrderIds(createOrderResp);
    if (!autoStatusCheck) return;
    if (!ids.orderUID && !ids.orderId) return;

    checkOrderStatus().catch(() => {});
    const t = setInterval(() => {
      checkOrderStatus().catch(() => {});
    }, 12000);

    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrderResp, autoStatusCheck, lang]);

  return (
    <div className="app">
      <div className="header">
        <div>
          <h1 className="title">Poteme API Test — Wallet + Exchanger (Invoice)</h1>
          <p className="subtitle">Routes → Route details → Create order (invoice) → Payment link + IPN + Auto status</p>
        </div>
        <div className="badges">
          <span className="badge">hash2: {authState?.hash2Algo || "?"}</span>
          <span className="badge">apikey: {authState?.apiKeyPrefix ? authState.apiKeyPrefix + "…" : "none"}</span>
          <span className="badge">secretLen: {authState?.secretLength ?? "?"}</span>
        </div>
      </div>

      <div className="twoCols">
        {/* LEFT */}
        <div className="panel">
          <div className="panelInner">
            <h3 className="sectionTitle">Auth / Wallet</h3>

            <div className="controls">
              <button className="btn" onClick={loadState} disabled={loading}>
                Auth state
              </button>

              <button className="btn" onClick={loadSession} disabled={loading}>
                Session get
              </button>
              <button className="btn" onClick={loadBalance} disabled={loading}>
                Load balance
              </button>
            </div>

            <div className="note">Wallet address helpers (network optional):</div>

            <div className="field">
              <label>Wallet</label>
              <select
                className="select"
                value={selectedWalletId || (wallets[0]?._id || "")}
                onChange={(e) => setSelectedWalletId(e.target.value)}
              >
                {wallets.map((w) => (
                  <option key={w._id} value={w._id}>
                    {w.currency?.symbol || "?"} — {w.currency?.name || "wallet"} — balance: {w.balance}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Network (empty = default)</label>
              <input
                className="input"
                value={network}
                onChange={(e) => setNetwork(e.target.value)}
                placeholder="TRC20 / ERC20 / (empty)"
              />
            </div>

            <div className="hintRow">
              <button className="btn btnSecondary" onClick={loadAddress} disabled={loading || !selectedWallet?._id}>
                Get address
              </button>
              <button className="btn btnSecondary" onClick={loadAddressAuto} disabled={loading || !selectedWallet?._id}>
                Address AUTO
              </button>
            </div>

            <details>
              <summary className="note">Debug responses (auth/login/session/balance/address)</summary>
              <pre className="mono">
                {pretty({ authState, sessionResp, balanceResp, addressResp, addressAutoResp })}
              </pre>
            </details>
          </div>
        </div>

        {/* RIGHT */}
        <div className="panel">
          <div className="panelInner">
            <h3 className="sectionTitle">Exchanger / Invoice (Create Order)</h3>

            <div className="field">
              <label>Language</label>
              <select className="select" value={lang} onChange={(e) => setLang(e.target.value)}>
                <option value="en">en</option>
                <option value="ua">ua</option>
                <option value="uk">uk</option>
                <option value="ru">ru</option>
              </select>
            </div>

            <div className="hintRow">
              <button className="btn" onClick={loadRoutes} disabled={loading}>
                Load routes
              </button>
              <button className="btn btnSecondary" onClick={() => loadRouteOne()} disabled={loading || !selectedRouteId}>
                Load route details
              </button>

              {authState?.depositRouteId && (
                <button
                  className="btn btnSecondary"
                  onClick={() => {
                    setSelectedRouteId(authState.depositRouteId);
                    loadRouteOne(authState.depositRouteId).catch(() => {});
                    setRouteSearch("tether trc-20 usdt wallet");
                  }}
                  disabled={loading}
                  title={authState.depositRouteId}
                >
                  Use deposit route (TRC20 → USDT Wallet)
                </button>
              )}
            </div>

            <div className="twoCols">
              <div className="field">
                <label>Search route (from/to/id)</label>
                <input
                  className="input"
                  value={routeSearch}
                  onChange={(e) => setRouteSearch(e.target.value)}
                  placeholder="tether trc-20 usdt wallet"
                />
              </div>
              <div className="field">
                <label>Show first N routes</label>
                <input className="input" value={routesLimit} onChange={(e) => setRoutesLimit(e.target.value)} />
              </div>
            </div>

            <div className="note">
              Total routes: {Array.isArray(routesRaw) ? routesRaw.length : 0} | Filtered: {routesFiltered.length} | Showing:{" "}
              {routesShown.length}
            </div>

            <div className="field">
              <label>Route</label>
              <select
  className="select"
  value={selectedRouteId}
  onChange={(e) => {
    const id = e.target.value;
    setSelectedRouteId(id);
    loadRouteOne(id).catch(() => {});
  }}
>

                <option value="">— select —</option>
                {routesShown.map((r) => {
  const rid = String(r.routeId || r._id || "").trim();
  return (
    <option key={rid} value={rid}>
      {(r.from?.name || r.from?.symbol || r.from?.xml || "?")} →{" "}
      {(r.to?.name || r.to?.symbol || r.to?.xml || "?")} | {rid}
    </option>
  );
})}

              </select>
            </div>

            <div className="twoCols">
              <div className="field">
                <label>Amount (in)</label>
                <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="field">
                <label>Partner (optional, can be empty)</label>
                <input className="input" value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="" />
              </div>
            </div>

            <div className="twoCols">
              <div className="field">
                <label>clientCallbackUrl (can be empty)</label>
                <input className="input" value={clientCallbackUrl} onChange={(e) => setClientCallbackUrl(e.target.value)} />
              </div>
              <div className="field">
                <label>ipnUrl (your webhook)</label>
                <input className="input" value={ipnUrl} onChange={(e) => setIpnUrl(e.target.value)} />
              </div>
            </div>

            <div className="twoCols">
              <div className="field">
                <label>ipnSecret</label>
                <input className="input" value={ipnSecret} onChange={(e) => setIpnSecret(e.target.value)} />
                <div className="hintRow">
                  <button className="btn btnSecondary" onClick={() => setIpnSecret(genSecret())} disabled={loading}>
                    Generate
                  </button>
                </div>
              </div>

              <div className="controls">
                <label className="note">
                  <input type="checkbox" checked={hideOutData} onChange={(e) => setHideOutData(e.target.checked)} />{" "}
                  hideOutData
                </label>
                <label className="note">
                  <input
                    type="checkbox"
                    checked={disableEmailNotify}
                    onChange={(e) => setDisableEmailNotify(e.target.checked)}
                  />{" "}
                  disableEmailNotify
                </label>
                <label className="note">
                  <input type="checkbox" checked={skipPreview} onChange={(e) => setSkipPreview(e.target.checked)} />{" "}
                  skipPreview
                </label>
              </div>
            </div>

            <div className="note">
              Поля ниже автоматически подтягиваются из route details. Required поля нужно заполнить, иначе order/create вернёт ошибку.
            </div>

            <details open>
              <summary className="note">Route fields (routeValues)</summary>
              {(routeFields || []).length === 0 ? (
                <div className="note">No route.fields</div>
              ) : (
                (routeFields || []).map((f) => (
                  <div className="field" key={f._id}>
                    <label>
                      {f.name} {f.required ? "(required)" : "(optional)"} — {String(f._id)}
                    </label>
                    <input
                      className="input"
                      value={routeValueMap[String(f._id)] ?? ""}
                      onChange={(e) => updateMap(setRouteValueMap, f._id, e.target.value)}
                      placeholder={f.regexp ? `regexp: ${f.regexp}` : ""}
                    />
                  </div>
                ))
              )}
            </details>

            <details>
              <summary className="note">From fields (fromValues)</summary>
              {(fromFields || []).length === 0 ? (
                <div className="note">No route.from.fields</div>
              ) : (
                (fromFields || []).map((f) => (
                  <div className="field" key={f._id}>
                    <label>
                      {f.name} {f.required ? "(required)" : "(optional)"} — {String(f._id)}
                    </label>
                    <input
                      className="input"
                      value={fromValueMap[String(f._id)] ?? ""}
                      onChange={(e) => updateMap(setFromValueMap, f._id, e.target.value)}
                      placeholder={f.regexp ? `regexp: ${f.regexp}` : ""}
                    />
                  </div>
                ))
              )}
            </details>

            <details>
              <summary className="note">To fields (toValues)</summary>
              {(toFields || []).length === 0 ? (
                <div className="note">No route.to.fields</div>
              ) : (
                (toFields || []).map((f) => (
                  <div className="field" key={f._id}>
                    <label>
                      {f.name} {f.required ? "(required)" : "(optional)"} — {String(f._id)}
                    </label>
                    <input
                      className="input"
                      value={toValueMap[String(f._id)] ?? ""}
                      onChange={(e) => updateMap(setToValueMap, f._id, e.target.value)}
                      placeholder={f.regexp ? `regexp: ${f.regexp}` : ""}
                    />
                  </div>
                ))
              )}
            </details>

            <div className="hintRow">
              <button className="btn" onClick={createOrder} disabled={loading || !selectedRouteId}>
                Create order (invoice)
              </button>
              <button className="btn btnSecondary" onClick={loadIpnEvents} disabled={loading}>
                Load IPN events
              </button>
            </div>

            {links.paymentUrl && (
              <div className="panel" style={{ marginTop: 12 }}>
                <div className="panelInner">
                  <div className="kv">
                    <div className="k">Payment URL</div>
                    <div className="v mono">{links.paymentUrl}</div>
                    <div className="k">Order URL</div>
                    <div className="v mono">{links.orderUrl}</div>
                  </div>

                  <div className="hintRow" style={{ marginTop: 12 }}>
                    <button
                      className="btn"
                      onClick={() => window.open(links.paymentUrl, "_blank", "noopener,noreferrer")}
                      disabled={!links.paymentUrl}
                    >
                      Open payment (USDT)
                    </button>

                    <label className="note" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={showPaymentIframe}
                        onChange={(e) => setShowPaymentIframe(e.target.checked)}
                      />
                      Embed payment in page
                    </label>
                  </div>

                  {/* Auto status */}
                  <div className="note" style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <strong>Order status:</strong> <span className="mono">{orderStatus || "—"}</span>
                      <label className="note" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={autoStatusCheck}
                          onChange={(e) => setAutoStatusCheck(e.target.checked)}
                        />
                        Auto-check
                      </label>
                      <button className="btn btnSecondary" onClick={checkOrderStatus} disabled={loading}>
                        Check now
                      </button>
                    </div>

                    {orderStatus === "done" && (
                      <div style={{ marginTop: 6 }}>
                        <strong>Оплачено / зачислено (done)</strong>
                      </div>
                    )}
                    {orderStatus === "waitPayment" && <div style={{ marginTop: 6 }}>Ожидаем оплату (waitPayment)</div>}
                    {orderStatus === "inProgress" && <div style={{ marginTop: 6 }}>В обработке (inProgress)</div>}
                    {orderStatus === "inProgressPayout" && (
                      <div style={{ marginTop: 6 }}>Очередь/подтверждения (inProgressPayout)</div>
                    )}
                    {["errorPayment", "errorPayout", "hold", "deleted", "returned"].includes(orderStatus) && (
                      <div style={{ marginTop: 6 }}>
                        <strong>Проблема: {orderStatus}</strong>
                      </div>
                    )}
                  </div>

                  {showPaymentIframe && (
                    <div style={{ marginTop: 12 }}>
                      <iframe
                        title="Poteme payment"
                        src={links.paymentUrl}
                        style={{
                          width: "100%",
                          height: 720,
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: 12,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <details>
              <summary className="note">Debug (compact)</summary>

              <div className="hintRow">
                <button className="btn btnSecondary" onClick={() => setShowFullRoutesJson((v) => !v)}>
                  {showFullRoutesJson ? "Hide full routes JSON" : "Show full routes JSON"}
                </button>
              </div>

              <pre className="mono">
                {pretty({
                  routesSummary: routesResp ? summarizeRoutes(routesResp) : null,
                  selectedRouteId,
                  routeOneResp,
                  createOrderResp,
                  ipnEventsResp,
                  orderStatusRaw,
                })}
              </pre>

              {showFullRoutesJson && (
                <pre className="mono" style={{ marginTop: 12 }}>
                  {pretty(routesResp)}
                </pre>
              )}
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}


 // pull defaults from server env + auto-select deposit route (if configured)